import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';

export type Platform = 'android' | 'ios' | 'web';

interface FileWriteOptions {
  fileName: string;
  data: string; // base64 sin prefijo
  mimeType?: string;
}

interface FileWriteResult {
  success: boolean;
  path?: string;
  uri?: string;
  error?: string;
}

const DIRECTORIES = {
  android: Directory.Documents,
  ios: Directory.Documents,
  web: Directory.Cache,
} as const;

const SUBDIRECTORY = 'FielPet';

export const getPlatform = (): Platform => {
  const platform = Capacitor.getPlatform();
  if (platform === 'android' || platform === 'ios') return platform;
  return 'web';
};

export const isNative = (): boolean => {
  return Capacitor.isNativePlatform();
};

const ensureDirectory = async (platform: Platform): Promise<void> => {
  if (platform === 'web') return;

  try {
    await Filesystem.mkdir({
      path: SUBDIRECTORY,
      directory: DIRECTORIES[platform],
      recursive: true,
    });
  } catch (e: any) {
    // Directory exists - ignore
    if (!e.message?.includes('exists')) throw e;
  }
};

const stripBase64Prefix = (dataUrl: string): string => {
  const idx = dataUrl.indexOf(',');
  return idx !== -1 ? dataUrl.substring(idx + 1) : dataUrl;
};

export const writeFile = async (options: FileWriteOptions): Promise<FileWriteResult> => {
  const platform = getPlatform();
  const { fileName, data, mimeType = 'image/png' } = options;

  if (platform === 'web') {
    return writeFileWeb(data, fileName, mimeType);
  }

  try {
    await ensureDirectory(platform);

    const filePath = `${SUBDIRECTORY}/${fileName}`;
    const result = await Filesystem.writeFile({
      path: filePath,
      data: stripBase64Prefix(data),
      directory: DIRECTORIES[platform],
      recursive: true,
    });

    return {
      success: true,
      path: filePath,
      uri: result.uri,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Error al guardar archivo',
    };
  }
};

const writeFileWeb = (data: string, fileName: string, mimeType: string): FileWriteResult => {
  try {
    const link = document.createElement('a');
    link.href = data.startsWith('data:') ? data : `data:${mimeType};base64,${data}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return { success: true, path: fileName };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getFilePath = (fileName: string): string => {
  const platform = getPlatform();

  switch (platform) {
    case 'android':
      return `file:///storage/emulated/0/Documents/${SUBDIRECTORY}/${fileName}`;
    case 'ios':
      return `${SUBDIRECTORY}/${fileName}`;
    default:
      return fileName;
  }
};

export const deleteFile = async (fileName: string): Promise<boolean> => {
  const platform = getPlatform();
  if (platform === 'web') return true;

  try {
    await Filesystem.deleteFile({
      path: `${SUBDIRECTORY}/${fileName}`,
      directory: DIRECTORIES[platform],
    });
    return true;
  } catch {
    return false;
  }
};

export const fileExists = async (fileName: string): Promise<boolean> => {
  const platform = getPlatform();
  if (platform === 'web') return false;

  try {
    await Filesystem.stat({
      path: `${SUBDIRECTORY}/${fileName}`,
      directory: DIRECTORIES[platform],
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * Convierte una cadena (data URL o blob URL) a un objeto File
 */
export const stringToFile = async (str: string, prefix = 'file'): Promise<File> => {
  const timestamp = Date.now();

  if (str.startsWith('data:')) {
    const arr = str.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], `${prefix}_${timestamp}.jpg`, { type: mime });
  }

  const response = await fetch(str);
  const blob = await response.blob();
  return new File([blob], `${prefix}_${timestamp}.jpg`, {
    type: blob.type || 'image/jpeg',
  });
};
